import {SectionMetaData} from '../interfaces/section-meta-data';
import {StringUtil} from './string-util';
import {DateUtil} from './date-util';
import {NumberUtil} from './number-util';
import {SchedulingTermRI} from '../rest-interfaces/scheduling-term-ri';
import {i18n} from './i18n-util';

export namespace SectionUtil {
  export type SectionComparator = (o1: SectionMetaData, o2: SectionMetaData) => number;

  // types used internally by section-utils
  type SectionMutator = (section: SectionMetaData) => void;

  // sections comparators
  function getPeriodDayComparator(termMap: Map<string, SchedulingTermRI>): SectionComparator {
    return (o1: SectionMetaData, o2: SectionMetaData): number => {
      let term1: SchedulingTermRI | undefined;
      let term2: SchedulingTermRI | undefined;

      // sort by school names first
      let comparedResult = StringUtil.compareStrings(o1.schoolName, o2.schoolName);
      if (comparedResult !== 0) {
        return comparedResult;
      }
      // then sort by school uid
      comparedResult = StringUtil.compareStrings(o1.schoolUid, o2.schoolUid);
      if (comparedResult !== 0) {
        return comparedResult;
      }

      // then sort by period day expression
      if (o1.periodDay == null && o2.periodDay == null) {
        return 0;
      } else if (o1.periodDay == null) {
        return 1;
      } else if (o2.periodDay == null) {
        return -1;
      } else {  // both periodDays exist, sort by first period, then by first cycle day.
        comparedResult = StringUtil.compareStrings(parseFirstPeriod(o1.periodDay), parseFirstPeriod(o2.periodDay));
        if (comparedResult !== 0) {
          return comparedResult;
        }

        comparedResult = StringUtil.compareStrings(parseFirstCycleDay(o1.periodDay), parseFirstCycleDay(o2.periodDay));
        if (comparedResult !== 0) {
          return comparedResult;
        }
      }

      // then sort by courseName
      comparedResult = compareCourseName(o1, o2);
      if (comparedResult !== 0) {
        return comparedResult;
      }

      //then sort by scheduling term
      term1 = o1.schedulingTerm != null ? termMap.get(o1.schedulingTerm.uid) : undefined;
      term2 = o2.schedulingTerm != null ? termMap.get(o2.schedulingTerm.uid) : undefined;
      //Nulls to the end
      if (term1 == null && term2 == null) {
        return 0;
      } else if (term1 == null) {
        return 1;
      } else if (term2 == null) {
        return -1;
      }

      let term1Date = term1.startDate instanceof Date ? term1.startDate : i18n.parseJsonDate(term1.startDate)!;
      let term2Date = term2.startDate instanceof Date ? term2.startDate : i18n.parseJsonDate(term2.startDate)!;

      comparedResult = DateUtil.compareDates(term1Date, term2Date);
      if (comparedResult !== 0) {
        return comparedResult;
      }

      term1Date = term1.endDate instanceof Date ? term1.endDate : i18n.parseJsonDate(term1.endDate)!;
      term2Date = term2.endDate instanceof Date ? term2.endDate : i18n.parseJsonDate(term2.endDate)!;
      comparedResult = DateUtil.compareDates(term1Date, term2Date);
      if (comparedResult !== 0) {
        return comparedResult;
      }

      return compareSourceSystemId(o1, o2);
    };
  }

  function getSectionNumberComparator(): SectionComparator {
    return (o1: SectionMetaData, o2: SectionMetaData): number => {
      let compareTo: number = compareSectionNumber(o1, o2);
      if (compareTo !== 0) {
        return compareTo;
      }

      compareTo = compareCourseName(o1, o2);
      if (compareTo !== 0) {
        return compareTo;
      }

      return compareSourceSystemId(o1, o2);
    };
  }

  function getSectionNameComparator(): SectionComparator {
    return (o1: SectionMetaData, o2: SectionMetaData): number => {
      let compare = compareCourseName(o1, o2);
      if (compare !== 0) {
        return compare;
      }

      compare = compareSectionNumber(o1, o2);
      if (compare !== 0) {
        return compare;
      }

      return compareSourceSystemId(o1, o2);
    };
  }

  // section mutators
  function periodAndDayMutator(section: SectionMetaData): void {
    section.primaryDisplay = section.preferredCourseName || section.courseName;
    section.secondaryDisplay = section.periodDay;
    section.classDisplayPreference = 'PeriodAndDay';
  }

  function customPeriodAndDayMutator(section: SectionMetaData): void {
    periodAndDayMutator(section);
    section.classDisplayPreference = 'CustomPeriodAndDayMutator';
    // once some provisioning work is done we need to do something like this here:
    // section.primaryDisplay = section.customCourseName;
  }

  function customMutator(section: SectionMetaData): void {
    sectionNumberMutator(section);
    section.classDisplayPreference = 'Custom';
    // once some provisioning work is done we need to do something like this here:
    // section.primaryDisplay = section.customCourseName;
  }

  function sectionNumberMutator(section: SectionMetaData): void {
    section.primaryDisplay = section.preferredCourseName || section.courseName;
    section.secondaryDisplay = section.sectionNumber;
    section.classDisplayPreference = 'SectionNumber';
  }

  // preference application functions
  export function setDisplayValuesByPreference(sections: Array<SectionMetaData>, classDisplayPrefValue: string): void {
    let mutator: SectionMutator;
    switch (classDisplayPrefValue) {
      case 'Custom':  // eventually custom will need to use a different mutator but we don't have section nickname yet.
      case 'SectionNumber':
      case 'Alphabetical':
        mutator = sectionNumberMutator;
        break;
      case 'CustomPeriodDay': // eventually custom period day will need to use a different mutator but we don't have section nickname yet.
      case 'PeriodAndDay':
      default:
        mutator = periodAndDayMutator;
        break;
    }
    sections.forEach(mutator);
  }

  export function getSortSectionByPreferenceComparator(classDisplayPrefValue: string, termMap?: Map<string, SchedulingTermRI>): SectionComparator {
    switch (classDisplayPrefValue) {
      case 'SectionNumber':
        return getSectionNumberComparator();
      case 'Custom': // eventually custom will need to use a different comparator but we don't have section nickname yet.
      case  'Alphabetical':
        return getSectionNameComparator();
      case 'CustomPeriodDay': // eventually custom period day will need to use a different comparator but we don't have section nickname yet.
      case 'PeriodAndDay':
      default:
        if (termMap == null) {
          throw Error('tried to sort by period day without passing in a termMap');
        }
        return getPeriodDayComparator(<Map<string, SchedulingTermRI>>termMap);
    }
  }

  export function sortSectionsByPreference(sections: Array<SectionMetaData>, classDisplayPrefValue: string, termMap?: Map<string, SchedulingTermRI>): void {
    let comparator = getSortSectionByPreferenceComparator(classDisplayPrefValue, termMap);
    //s.schoolName asc, s.schoolUid asc, s.periodDay asc, s.courseName asc, s.schedulingTerm asc
    sections.sort(comparator);
  }

  // general utility methods
  function compareSectionNumber(o1: SectionMetaData,  o2: SectionMetaData): number {
    let sectionNumber1: string = o1.sectionNumber != null ? o1.sectionNumber.trim() : '';
    let sectionNumber2: string = o2.sectionNumber != null ? o2.sectionNumber.trim() : '';

    let section1AsNumber: number = parseInt(sectionNumber1);
    let section2AsNumber: number = parseInt(sectionNumber2);

    let compareTo: number;
    if (NumberUtil.isNumber(section1AsNumber) && NumberUtil.isNumber(section2AsNumber)) {
      compareTo = NumberUtil.compareNumbers(section1AsNumber, section2AsNumber);
    } else if (!NumberUtil.isNumber(section1AsNumber) && !NumberUtil.isNumber(section2AsNumber)) {
      compareTo = StringUtil.compareStringsIgnoreCase(sectionNumber1, sectionNumber2);
    } else if (NumberUtil.isNumber(section1AsNumber) && !NumberUtil.isNumber(section2AsNumber)) {
      return -1;
    } else {
      return 1;
    }

    return compareTo;
  }

  function compareSourceSystemId(o1, o2): number {
    let sourceSystemId1 = parseInt(o1.sourceSystemId);
    let sourceSystemId2 = parseInt(o2.sourceSystemId);
    if (!NumberUtil.isNumber(sourceSystemId1) || !NumberUtil.isNumber(sourceSystemId2)) {
      return StringUtil.compareStringsIgnoreCase(o1.sourceSystemId, o2.sourceSystemId);
    }
    return NumberUtil.compareNumbers(sourceSystemId1, sourceSystemId2);
  }

  function compareCourseName(o1: SectionMetaData,  o2: SectionMetaData): number {
    let courseName1: string = o1.preferredCourseName ? StringUtil.defaultString(o1.preferredCourseName).trim() : StringUtil.defaultString(o1.courseName).trim();
    let courseName2: string = o2.preferredCourseName ? StringUtil.defaultString(o2.preferredCourseName).trim() : StringUtil.defaultString(o2.courseName).trim();
    return StringUtil.compareStringsIgnoreCase(courseName1, courseName2);
  }

  function parseFirstPeriod(periodDayExpression: string): string {
    let upToParen: string = periodDayExpression.substr(0, periodDayExpression.indexOf('('));
    if (upToParen !== periodDayExpression) {
      let hyphenIndex = upToParen.indexOf('-');
      if (hyphenIndex !== -1) {
        return upToParen.substr(0, hyphenIndex);
      }
      return upToParen;
    }
    return periodDayExpression;
  }

  function parseFirstCycleDay(periodDayExpression: string): string {
    let firstCycleDays = periodDayExpression.substr(periodDayExpression.indexOf('(') + 1, periodDayExpression.indexOf(')') - 1);
    if (firstCycleDays !== periodDayExpression) {
      let charIndex = firstCycleDays.indexOf(',');
      if (charIndex !== -1) {
        return firstCycleDays.substr(0, charIndex);
      }
      charIndex = firstCycleDays.indexOf('-');
      if (charIndex !== -1) {
        return firstCycleDays.substr(0, charIndex);
      }
      return firstCycleDays;
    }
    return periodDayExpression;
  }
}
